import { useState, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../store";
import { useMaxUiKpis } from "../common/hooks/useMaxUiKpis";
import { getCanonicalTitle } from "../../../common/util/Incentives/IncentiveRegistry";
import { selectGroupLeader, setSelectedLeader } from "../../../reducers/groupLeaderFilter";
import { useGroupLeaderUrlSync } from "../../../common/hooks/useGroupLeaderUrlSync";
import { ActionReportHeader } from "../common/Header/ActionReportHeader";
import { ActionReportToolbar } from "../common/Toolbar/ActionReportToolbar";
import { ActionReportListView, ActionReportListViewLoader } from "../common/ActionReportListView/ActionReportListView";
import LeafRectangleLoader from "../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { GroupLeaderFilter } from "../common/GroupLeaderFilter/GroupLeaderFilter";
import { GroupLeaderFilterErrorBoundary } from "../common/GroupLeaderFilter/GroupLeaderFilterErrorBoundary";
import { ERROR_TYPE, ErrorPage, IconType } from "../../ErrorPage/ErrorPage";
import ServerSideExportInProgressNotify from "../../ServerSideExport/ServerSideExportCommon/ServerSideExportNotify/ServerSideExportInProgressNotify/ServerSideExportInProgressNotify";
import ServerSideExportCompletedNotify from "../../ServerSideExport/ServerSideExportCommon/ServerSideExportNotify/ServerSideExportCompletedNotify/ServerSideExportCompletedNotify";
import { dispatchOpenExportV2ModalEvent } from "../../ServerSideExport/ServerSideExportCommon/ServerSideExportUtil/ServerSideExportEvents";
import { CollapsibleSection } from "../common/CollapsibleSection/CollapsibleSection";
import { FetchSaqActionReportData } from "./SaqActionReportDataLayer";
import type { SaqReportRecord } from "./SaqActionReportMockData";
import { MODULE_SAQ, MODULE_SAQ_EXPORT_NAME } from "../../../common/constants/export-constants";
import {
  buildFieldTitleOverrides,
  buildBaseExportPayload,
  isExportDisabledBySessionSsoOrigin,
} from "../../../common/util/exportUtil";
import { useSaqExportV2Mutation } from "../../../services/ExportV2/exportV2API";
import type { SaqExportAffABO, TextOverride } from "../../../services/ExportV2/exportV2API.types";
import serverSideExportContent from "../../../content/serverSideExport";
import { recordsInLeaderSubtree, leaderExistsInTree, parentLeaderOf } from "../common/groupLeaderGrouping";
import defaultContent from "../../../content/actionReportsSaq";
import type { RecordRef } from "../../../services/ActionReports/ActionReportAPI.types";
import type {
  SearchSelectItem,
  SearchCallback,
  SelectionCallback,
} from "../../../common/components/SearchSelect/SearchSelect";
import { KPI_ID } from "../../../services/AboEnrichment/AboEnrichmentAPI.types";
import { useGetSavedKpiPreferenceQuery } from "../../../services/KPIPreferences/savedKpiPreferencesAPI";
import {
  saqPageViewTrack,
  saqTabClickTrack,
  saqExportTrack,
  saqCustomizeKpiTrack,
  saqCancelCustomizeKpiTrack,
  saqApplyCustomizeKpiTrack,
  saqProfileTrack,
  saqAddToWatchlistTrack,
  saqRemoveFromWatchlistTrack,
  saqPhoneIconTrack,
  saqMessageIconTrack,
  saqSearchTrack,
  saqSelectGroupLeaderTrack,
} from "../../../common/tealium/ActionReports/SaqTrack";
import "../ActionReports.scss";
import "./SaqActionReport.scss";
import { getCountryNameFromCode } from "../../../common/util/formatCountryName";
import { storedLocale } from "../../Authentication/util";
import { getDisplayName } from "../../../common/util/displayName";

export type SaqTab = "incomplete" | "completed" | "upForRenewal";

const padAffSafe = (aff: string): string => {
  if (aff.length >= 3) return aff;
  return /^\d+$/.test(aff) ? aff.padStart(3, "0") : aff;
};

export const filterByTab = (record: SaqReportRecord, tab: SaqTab | null): boolean => {
  if (!tab) return true;
  if (tab === "completed") return record.completed && !record.upForRenewal;
  if (tab === "incomplete") return !record.completed;
  if (tab === "upForRenewal") return record.upForRenewal;
  return true;
};

/**
 * Formats an ISO date string (e.g. "2026-12-31T00:00:00+09:00") to a locale-aware date string.
 * The API returns the date already adjusted to the user's timezone, so the browser's local
 * Intl.DateTimeFormat produces the correct formatted date (e.g. "2026/12/31" for ja-JP,
 * "12/31/2026" for en-US).
 * Returns empty string if input is empty or invalid.
 */
export const formatDueDate = (isoDate: string, locale: string): string => {
  if (!isoDate) return "";
  try {
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat(locale, {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    }).format(date);
  } catch {
    return "";
  }
};

const createSaqFieldConfig = (tableColumns: Record<string, string>) => [
  { id: KPI_ID.SAQ_STATUS, kpi: "saqStatus", title: tableColumns["saqStatus"] },
  { id: KPI_ID.SPONSORING_YTD, kpi: "sponsoringYtd", title: tableColumns["sponsoringYtd"] },
  { id: KPI_ID.GROUP_SIZE, kpi: "groupSize", title: tableColumns["groupSize"] },
  { id: KPI_ID.ANNUAL_PPV, kpi: "annualPPV", title: tableColumns["annualPPV"] },
  { id: "saqSponsorName", kpi: "sponsorName", title: tableColumns["sponsorName"] },
  { id: KPI_ID.PHONE_NUMBER, kpi: "phoneNumber", title: "" },
  { id: KPI_ID.WHATSAPP, kpi: "whatsapp", title: "" },
  { id: KPI_ID.IMAGE_REF_URL, kpi: "imageRefURL", title: "" },
];

const createSaqCompletedFieldConfig = (tableColumns: Record<string, string>) => [
  { id: KPI_ID.SAQ_STATUS, kpi: "saqStatus", title: tableColumns["saqStatus"] },
  { id: KPI_ID.SPONSORING_YTD, kpi: "sponsoringYtd", title: tableColumns["sponsoringYtd"] },
  { id: KPI_ID.GROUP_SIZE, kpi: "groupSize", title: tableColumns["groupSize"] },
  { id: KPI_ID.ANNUAL_PPV, kpi: "annualPPV", title: tableColumns["annualPPV"] },
  { id: "saqExpirationDate", kpi: "expirationDate", title: tableColumns["expirationDate"] },
  { id: "saqSponsorName", kpi: "sponsorName", title: tableColumns["sponsorName"] },
  { id: KPI_ID.PHONE_NUMBER, kpi: "phoneNumber", title: "" },
  { id: KPI_ID.WHATSAPP, kpi: "whatsapp", title: "" },
  { id: KPI_ID.IMAGE_REF_URL, kpi: "imageRefURL", title: "" },
];

// KPIs that are always visible (not user-toggleable)
const FIXED_KPIS = ["saqStatus", "sponsorName"];

// Default configurable KPIs (shown in the KPI selector dropdown)
const DEFAULT_CONFIGURABLE_KPIS = ["sponsoringYtd", "groupSize", "annualPPV"];

export const getConfiguredSelectedKpis = (
  savedKpis: { id: string; displaydefault: boolean }[] | undefined,
  availableKpis: string[],
): string[] => {
  const userKpis =
    !savedKpis || savedKpis.length === 0
      ? DEFAULT_CONFIGURABLE_KPIS.filter((kpi) => availableKpis.includes(kpi))
      : savedKpis.filter((kpi) => kpi.displaydefault && availableKpis.includes(kpi.id)).map((kpi) => kpi.id);

  // Always include fixed KPIs: saqStatus at the beginning, sponsorName at the end.
  return [...new Set(["saqStatus", ...userKpis, "sponsorName"])];
};

const useSelectedKpis = (availableKpis: string[]) => {
  const { savedKpiPreferencesData } = useAppSelector((state) => state.savedKpiPreferencesData);
  useGetSavedKpiPreferenceQuery("saqKPIs", { refetchOnMountOrArgChange: true });

  return useMemo(
    () => getConfiguredSelectedKpis(savedKpiPreferencesData?.savedDataViews?.saqKPIs?.availableKpis, availableKpis),
    [savedKpiPreferencesData, availableKpis],
  );
};

export default function SaqActionReport() {
  const { t, i18n } = useTranslation(["actionReportsSaq", "actionReports", "serverSideExport", "common"]);
  const maxUiKpis = useMaxUiKpis();
  const { selectedPeriod } = useAppSelector((state) => state.period);
  const boot = useAppSelector((state) => state.boot);
  const { configuration, user } = boot;
  const { los: isExportDisabled } = isExportDisabledBySessionSsoOrigin();
  const { uiKpis } = configuration;
  const { profile } = configuration;
  const enableGroupLeaderFilter =
    useAppSelector((state) => state.boot.configuration.actionReports?.enableGroupLeaderFilter) ?? false;
  const dispatch = useAppDispatch();
  const selectedLeader = useAppSelector(selectGroupLeader);

  // Missing config falls back; an explicitly empty list disables configurable KPIs.
  const availableKpis = useMemo(() => {
    const configKpis = uiKpis.saqAr;
    if (configKpis === undefined || configKpis === null) return DEFAULT_CONFIGURABLE_KPIS;
    return configKpis.filter((kpi) => !FIXED_KPIS.includes(kpi));
  }, [uiKpis.saqAr]);

  const [activeTab, setActiveTab] = useState<SaqTab | null>(null);

  const selectedKpis = useSelectedKpis(availableKpis);

  // SAQ export mutation
  const [saqExport] = useSaqExportV2Mutation();

  const { saqActionReportData, isLoading, errorStatus } = FetchSaqActionReportData(selectedPeriod);

  const groupLeaders = useMemo(() => saqActionReportData.groupLeaders ?? [], [saqActionReportData.groupLeaders]);
  const groupLeaderTree = useMemo(
    () => saqActionReportData.groupLeaderTree ?? [],
    [saqActionReportData.groupLeaderTree],
  );
  useGroupLeaderUrlSync(isLoading ? undefined : saqActionReportData.groupLeaders);

  // Search state
  const [searchText, setSearchText] = useState<string>("");
  const [scrollToAbo, setScrollToAbo] = useState<{ aff: string; abo: string } | undefined>(undefined);

  const tabs: { key: SaqTab; labelKey: keyof typeof defaultContent.tabs; hidden?: boolean }[] = [
    { key: "upForRenewal", labelKey: "upForRenewal", hidden: !saqActionReportData.showUpForSAQRenewal },
    { key: "incomplete", labelKey: "notQualified" },
    { key: "completed", labelKey: "qualified" },
  ];

  const startedByLabels = useMemo<Record<string, string>>(
    () => ({
      [`${MODULE_SAQ_EXPORT_NAME}_Incomplete`]: t("tabs.notQualified", defaultContent.tabs.notQualified).toString(),
      [`${MODULE_SAQ_EXPORT_NAME}_Completed`]: t("tabs.qualified", defaultContent.tabs.qualified).toString(),
      [`${MODULE_SAQ_EXPORT_NAME}_UpForRenewal`]: t("tabs.upForRenewal", defaultContent.tabs.upForRenewal).toString(),
    }),
    [t],
  );

  // Page view tracking — fires once per period change after data loads
  const [pageViewFired, setPageViewFired] = useState(false);
  useEffect(() => {
    if (!isLoading && !pageViewFired && activeTab !== null) {
      saqPageViewTrack(selectedPeriod);
      setPageViewFired(true);
    }
  }, [isLoading, pageViewFired, activeTab, selectedPeriod]);

  // Reset page view guard when period changes
  useEffect(() => {
    setPageViewFired(false);
  }, [selectedPeriod]);

  // Auto-select the first visible tab once data loads
  useEffect(() => {
    if (!isLoading && saqActionReportData.saq.length > 0 && activeTab === null) {
      const firstVisibleTab = tabs.find((tab) => !tab.hidden);
      if (firstVisibleTab) {
        setActiveTab(firstVisibleTab.key);
      }
    }
    // tabs is derived from saqActionReportData, so showUpForSAQRenewal covers it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, saqActionReportData.saq.length, saqActionReportData.showUpForSAQRenewal, activeTab]);

  // Group Leader filtering helpers
  const getFirstGroupLeader = (r: SaqReportRecord): RecordRef | undefined => r.firstGroupLeader;
  const getSelfRef = (r: SaqReportRecord): RecordRef => ({ aff: r.aff, abo: r.abo });

  const activeRecords: SaqReportRecord[] = useMemo(() => {
    const raw = saqActionReportData.saq ?? [];

    // Apply tab filter based on API-provided completed/upForRenewal fields
    const tabFiltered = raw.filter((record) => filterByTab(record, activeTab));

    // Apply group leader filter
    const filtered =
      selectedLeader && groupLeaders.length > 0
        ? recordsInLeaderSubtree(tabFiltered, groupLeaderTree, selectedLeader, getSelfRef, getFirstGroupLeader)
        : tabFiltered;

    return filtered;
  }, [saqActionReportData, activeTab, selectedLeader, groupLeaders, groupLeaderTree]);

  const getTabCount = useCallback(
    (tabKey: SaqTab): number => {
      const raw = saqActionReportData.saq ?? [];
      switch (tabKey) {
        case "incomplete":
          return raw.filter((r) => !r.completed).length;
        case "completed":
          return raw.filter((r) => r.completed && !r.upForRenewal).length;
        case "upForRenewal":
          return raw.filter((r) => r.upForRenewal).length;
        default:
          return 0;
      }
    },
    [saqActionReportData.saq],
  );

  const totalCount = useMemo(() => {
    return saqActionReportData.saq?.length ?? 0;
  }, [saqActionReportData]);

  const tableColumns = useMemo(() => {
    const columns = t("table.columns", {
      ns: "actionReportsSaq",
      returnObjects: true,
      defaultValue: defaultContent.table.columns,
    }) as Record<string, string>;
    return columns;
  }, [t]);

  const fieldConfig = useMemo(() => {
    return activeTab === "completed" ? createSaqCompletedFieldConfig(tableColumns) : createSaqFieldConfig(tableColumns);
  }, [tableColumns, activeTab]);

  const fieldTitleOverrides = useMemo(() => buildFieldTitleOverrides(tableColumns), [tableColumns]);

  // Export data: ABOs filtered by active tab with SAQ-specific metadata
  const saqExportAffAbos: SaqExportAffABO[] = useMemo(() => {
    const allRecords = saqActionReportData.saq ?? [];
    const filtered = allRecords.filter((record) => filterByTab(record, activeTab));
    return filtered.map((record) => ({
      affAbo: `${record.aff.padStart(3, "0")}-${record.abo}`,
      level: record.level,
      sponsorAbo: "",
      completed: record.completed ?? false,
      dueDate: record.dueDate ?? "",
      upForRenewal: record.upForRenewal ?? false,
    }));
  }, [saqActionReportData, activeTab]);

  // Export AffAbos for the toolbar (base type without SAQ metadata)
  const exportAffAbos = useMemo(() => {
    return saqExportAffAbos.map(({ affAbo, level, sponsorAbo }) => ({
      affAbo,
      level,
      sponsorAbo,
    }));
  }, [saqExportAffAbos]);

  // Custom SAQ export trigger that uses the dedicated /api/saq endpoint
  const userAbo = user.abo;
  const userIsoCountryCode = user.isoCountryCode;
  const userAff = user.aff;
  const addressFormat = profile?.format;

  const customTriggerExport = useCallback(async () => {
    if (isExportDisabled) return;
    const isoCountryCode = userIsoCountryCode || "";
    const affAbo = `${userAff}-${userAbo}`;
    const { address, addressLinesDivider } = addressFormat || {};

    const textOverride = t("textOverride", {
      returnObjects: true,
      defaultValue: serverSideExportContent.textOverride,
      ns: "serverSideExport",
    }) as TextOverride;

    const exportFields = t("exportFields", {
      returnObjects: true,
      defaultValue: serverSideExportContent.exportFields,
      ns: "serverSideExport",
    }) as Record<string, string>;

    const exportKpis =
      activeTab === "completed"
        ? [
            "saqStatus",
            "expirationDate",
            ...selectedKpis.filter((kpi) => !FIXED_KPIS.includes(kpi) && kpi !== "saqStatus"),
            "sponsorName",
          ]
        : selectedKpis;

    const tabSuffix = activeTab ? `_${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}` : "";

    const basePayload = buildBaseExportPayload({
      moduleId: MODULE_SAQ,
      reportAffAbo: affAbo,
      period: selectedPeriod,
      startedBy: `${MODULE_SAQ_EXPORT_NAME}${tabSuffix}`,
      countryCode: isoCountryCode,
      addressPattern: address || "",
      addressLineDivider: addressLinesDivider || "",
      selectedKpis: exportKpis,
      textOverride,
      exportFields,
      fieldTitleOverrides,
    });

    await saqExport({
      ...basePayload,
      affAbos: saqExportAffAbos,
    }).unwrap();
  }, [
    userIsoCountryCode,
    userAff,
    userAbo,
    addressFormat,
    selectedPeriod,
    t,
    saqExport,
    saqExportAffAbos,
    fieldTitleOverrides,
    activeTab,
    selectedKpis,
    isExportDisabled,
  ]);

  // Determine which tab a record belongs to
  const getRecordTab = useCallback((record: SaqReportRecord): SaqTab => {
    if (record.upForRenewal) return "upForRenewal";
    if (record.completed) return "completed";
    return "incomplete";
  }, []);

  // Search pool always covers all records so search works across all tabs and group leader boundaries
  const searchPool = useMemo(() => {
    const allRecords = saqActionReportData.saq ?? [];
    return allRecords.map((record) => ({
      aff: record.aff,
      abo: record.abo,
      isoCountry: record.isoCountry,
      aboName: record.confidentialUser ? t("common:confidentialUser", "Confidential User") : record.name,
      firstGroupLeader: record.firstGroupLeader,
      tab: getRecordTab(record),
    }));
  }, [saqActionReportData, t, getRecordTab]);

  const handleSearchCallback = useCallback<SearchCallback>(
    (text) =>
      searchPool
        .filter((entry) => entry.aboName.toLowerCase().includes(text.toLowerCase()) || entry.abo.includes(text))
        .map((entry): SearchSelectItem => {
          const sublabel = `${entry.abo} | ${getCountryNameFromCode(entry.isoCountry, storedLocale())}`;
          return {
            key: `${entry.aff}-${entry.abo}`,
            label: entry.aboName,
            sublabel,
          };
        }),
    [searchPool],
  );

  const handleSelectionCallback = useCallback<SelectionCallback>(
    (key) => {
      const found = searchPool.find((entry) => `${entry.aff}-${entry.abo}` === key);
      if (found) {
        // Switch to the tab where the ABO exists (follows Renewals AR pattern)
        setActiveTab(found.tab);

        if (groupLeaders.length > 0) {
          const selfRef: RecordRef = { aff: found.aff, abo: found.abo };
          const isInTree = leaderExistsInTree(groupLeaderTree, selfRef);
          const fglMatchesSelf =
            !!found.firstGroupLeader &&
            padAffSafe(found.firstGroupLeader.aff) === padAffSafe(found.aff) &&
            found.firstGroupLeader.abo === found.abo;
          const isSelfLeader = isInTree || fglMatchesSelf;

          let targetLeader: RecordRef | null = null;
          if (isSelfLeader) {
            targetLeader = parentLeaderOf(groupLeaderTree, selfRef);
          } else if (found.firstGroupLeader?.aff || found.firstGroupLeader?.abo) {
            targetLeader = found.firstGroupLeader;
          }

          dispatch(setSelectedLeader(targetLeader));
        }

        setScrollToAbo({ aff: found.aff, abo: found.abo });
        setSearchText(found.aboName);
      }
      return { displayName: found?.aboName ?? "" };
    },
    [searchPool, groupLeaderTree, groupLeaders, dispatch],
  );

  const handleClearSearch = useCallback(() => {
    setSearchText("");
    setScrollToAbo(undefined);
  }, []);

  const notifyDownloadLinkOnClick = useCallback(() => {
    if (isExportDisabled) return;
    dispatchOpenExportV2ModalEvent(MODULE_SAQ, true);
  }, [isExportDisabled]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="SaqAR__content">
          <section className="SaqAR__sectionLoader">
            <LeafRectangleLoader isLoading height="1.75rem" width="8rem" margin="0" />
          </section>
          <ActionReportListViewLoader />
        </div>
      );
    }

    if (errorStatus) {
      return (
        <div className="SaqAR__error">
          <ErrorPage errorType={ERROR_TYPE.GENERIC_ERROR} iconType={IconType.SearchCriteria} />
        </div>
      );
    }

    if (activeRecords.length === 0) {
      return (
        <div className="SaqAR__noDownlines">
          <ErrorPage errorType={ERROR_TYPE.NO_DOWNLINES} iconType={IconType.SearchCriteria} />
        </div>
      );
    }

    // Helper to build report data from records
    const buildReportData = (records: typeof activeRecords) =>
      records.map((record) => ({
        aboName: record.confidentialUser ? t("common:confidentialUser", "Confidential User") : record.name,
        aboNumber: record.abo,
        abo: record.abo,
        aff: record.aff,
        isProvisional: false,
        isConfidential: record.confidentialUser,
        level: record.level,
        saqStatus: record.completed ? "qualified" : "incomplete",
        sponsoringYtd: "",
        groupSize: "",
        annualPPV: "",
        expirationDate: formatDueDate(record.dueDate, i18n.language),
        sponsorName: record.sponsor.confidentialUser
          ? t("common:confidentialUser", "Confidential User")
          : record.sponsor.name,
      }));

    // Helper to get selected KPIs with expirationDate for completed tab
    const getKpisForView = () => {
      const base = selectedKpis.filter((kpi) => FIXED_KPIS.includes(kpi) || availableKpis.includes(kpi));
      if (activeTab === "completed") {
        // Insert expirationDate before sponsorName
        const sponsorIdx = base.indexOf("sponsorName");
        if (sponsorIdx !== -1 && !base.includes("expirationDate")) {
          base.splice(sponsorIdx, 0, "expirationDate");
        }
      }
      return base;
    };

    // When no group leaders are available, render a flat list without CollapsibleSection
    if (groupLeaders.length === 0) {
      const reportData = buildReportData(activeRecords);

      return (
        <div className="SaqAR__content SaqAR__content--flat">
          <ActionReportListView
            selectedKpis={getKpisForView()}
            reportId="saqAR-flat"
            reportData={reportData}
            fieldConfig={fieldConfig}
            enableWatchlist={true}
            showImageBlock={true}
            aboHeader="nameNumber"
            skipEnrichment={false}
            scrollToIndex={
              scrollToAbo
                ? reportData.findIndex((r) => r.aff === scrollToAbo.aff && r.abo === scrollToAbo.abo)
                : undefined
            }
            onProfileTrack={saqProfileTrack}
            onWatchlistTrack={(isAdding) => {
              if (isAdding) {
                saqAddToWatchlistTrack();
              } else {
                saqRemoveFromWatchlistTrack();
              }
            }}
            onPhoneTrack={saqPhoneIconTrack}
            onMessageTrack={saqMessageIconTrack}
          />
        </div>
      );
    }

    // Group records by Group Leader when group leaders are available
    const groupedByLeader = activeRecords.reduce<Record<string, typeof activeRecords>>((acc, record) => {
      const gl = groupLeaders.find(
        (g) => g.aff === record.firstGroupLeader?.aff && g.abo === record.firstGroupLeader?.abo,
      );
      const key = gl ? `${gl.aff}-${gl.abo}` : "unknown";
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(record);
      return acc;
    }, {});

    return (
      <div className="SaqAR__content">
        {Object.entries(groupedByLeader)
          .filter(([leaderKey]) => leaderKey !== "unknown")
          .map(([leaderKey, records]) => {
            const gl = groupLeaders.find((g) => `${g.aff}-${g.abo}` === leaderKey);
            const leaderName = gl?.name ? getDisplayName(gl.name, t) : "";

            const groupReportData = buildReportData(records);

            return (
              <CollapsibleSection key={leaderKey} label={leaderName} count={records.length}>
                <ActionReportListView
                  selectedKpis={getKpisForView()}
                  reportId={`saqAR-${leaderKey}`}
                  reportData={groupReportData}
                  fieldConfig={fieldConfig}
                  enableWatchlist={true}
                  showImageBlock={true}
                  aboHeader="nameNumber"
                  skipEnrichment={false}
                  scrollToIndex={
                    scrollToAbo
                      ? groupReportData.findIndex((r) => r.aff === scrollToAbo.aff && r.abo === scrollToAbo.abo)
                      : undefined
                  }
                  onProfileTrack={saqProfileTrack}
                  onWatchlistTrack={(isAdding) => {
                    if (isAdding) {
                      saqAddToWatchlistTrack();
                    } else {
                      saqRemoveFromWatchlistTrack();
                    }
                  }}
                  onPhoneTrack={saqPhoneIconTrack}
                  onMessageTrack={saqMessageIconTrack}
                />
              </CollapsibleSection>
            );
          })}
      </div>
    );
  };

  const pageTitle = getCanonicalTitle({ id: "saqActionReport" }, t, {});

  return (
    <main className="SaqAR">
      <ActionReportHeader
        title={pageTitle}
        description={t("title.pageDescription", defaultContent.title.pageDescription).toString()}
        reportLength={totalCount}
      />
      <ActionReportToolbar
        reportLength={activeRecords.length}
        isAllEnriched={true}
        kpiSelector={{
          availableKpis: availableKpis,
          defaultKpis: DEFAULT_CONFIGURABLE_KPIS,
          saveViewName: "saqKPIs",
          saveViewDescription: "SAQ Action Report KPI Preferences",
          maxSelectionsAllowed: maxUiKpis,
          textOverrides: {
            sponsoringYtd: tableColumns["sponsoringYtd"],
            groupSize: tableColumns["groupSize"],
            annualPPV: tableColumns["annualPPV"],
          },
          disabled: isLoading,
          onOpenTrack: saqCustomizeKpiTrack,
          onCancelTrack: saqCancelCustomizeKpiTrack,
          onApplyTrack: saqApplyCustomizeKpiTrack,
        }}
        search={{
          enabled: true,
          searchCallback: handleSearchCallback,
          selectionCallback: handleSelectionCallback,
          searchText: searchText,
          clearFunction: handleClearSearch,
          disabled: isLoading || searchPool.length === 0,
          onFocus: saqSearchTrack,
        }}
        export={{
          moduleId: MODULE_SAQ,
          exportAffAbos,
          selectedKpis,
          confirmationMessage: t("export.description", defaultContent.export.description).toString(),
          exportType: MODULE_SAQ,
          startedByLabels,
          fieldTitleOverrides,
          customTriggerExport,
        }}
        exportTrack={saqExportTrack}
        groupLeaderFilterSlot={
          enableGroupLeaderFilter ? (
            isLoading ? (
              <GroupLeaderFilter leaders={[]} tree={[]} isLoading />
            ) : groupLeaders.length > 0 ? (
              <GroupLeaderFilterErrorBoundary>
                <GroupLeaderFilter
                  leaders={groupLeaders}
                  tree={groupLeaderTree}
                  allOptionLabel={t(
                    "filter.groupLeader.allOption",
                    defaultContent.filter.groupLeader.allOption,
                  ).toString()}
                  onSelectTrack={saqSelectGroupLeaderTrack}
                />
              </GroupLeaderFilterErrorBoundary>
            ) : undefined
          ) : undefined
        }
      />
      <div className="SaqAR__tabs" role="tablist">
        {isLoading ? (
          <div className="leaf-tab-views__loader">
            <LeafRectangleLoader isLoading={true} width="25rem" height="2rem" />
          </div>
        ) : (
          tabs
            .filter((tab) => !tab.hidden)
            .map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  role="tab"
                  aria-selected={isActive}
                  className={`SaqAR__tab${isActive ? " SaqAR__tab--active" : ""}`}
                  onClick={() => {
                    const nextTab = isActive ? null : tab.key;
                    if (nextTab) saqTabClickTrack(nextTab);
                    setActiveTab(nextTab);
                    setScrollToAbo(undefined);
                  }}
                >
                  {`${t(`tabs.${tab.labelKey}`, defaultContent.tabs[tab.labelKey])} (${getTabCount(tab.key)})`}
                </button>
              );
            })
        )}
      </div>
      {renderContent()}
      <ServerSideExportInProgressNotify />
      <ServerSideExportCompletedNotify moduleId={MODULE_SAQ} notifyDownloadLinkOnClick={notifyDownloadLinkOnClick} />
    </main>
  );
}
